import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { useId, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { pageMeta, ldScripts, breadcrumbLd, faqLd, SITE_ORIGIN, HQ_LOCALBUSINESS_ID, organizationLd, localBusinessLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { Picture } from "@/components/media/Picture";
import { AUSTIN_NEIGHBORHOODS } from "@/content/neighborhoods";
import { LOCATIONS } from "@/content/locations";
import { SERVICES } from "@/content/services";
import { trackConversion } from "@/lib/conversions";

type ContactSearch = { location: string; service: string };
function validateContactSearch(raw: Record<string, unknown>): ContactSearch {
  const loc = typeof raw.location === "string" ? raw.location.slice(0, 60) : "";
  const svc = typeof raw.service === "string" ? raw.service.slice(0, 60) : "";
  return { location: loc, service: svc };
}

export const Route = createFileRoute("/contact")({
  validateSearch: validateContactSearch,
  head: () => {
    const path = "/contact";
    const base = pageMeta({
      path,
      title: `Book a Free SEO Strategy Call — Contact ${siteConfig.name}`,
      description:
        `Book your free 30-minute ${siteConfig.contact.address.city} SEO strategy call. Get a mini-audit of your Google and AI search footprint today. Call ${siteConfig.contact.whatsapp.display}.`,
      image: "https://optimizeplus.net/og-image.png",

      type: "website",
    });
    return {
      ...base,
      scripts: ldScripts(
        localBusinessLd({
          city: siteConfig.contact.address.city,
          path: "/contact",
          description: `Get in touch with ${siteConfig.name} in ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state} for free SEO strategy calls.`,
          latLng: siteConfig.contact.address.coordinates

        }),
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Contact", path },
        ]),

        faqLd([
          {
            q: `How quickly does ${siteConfig.name} reply to contact requests?`,
            a: `We reply to every contact form submission within one business day, and offer 24/7 services for urgent SEO and AI-search support from our ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state} office at ${siteConfig.contact.address.street}.`,

          },
          {
            q: `Do you work with businesses outside ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state}?`,
            a: `Yes. We are headquartered in ${siteConfig.contact.address.city} at ${siteConfig.contact.address.street}, ${siteConfig.contact.address.state} ${siteConfig.contact.address.zip}, and serve clients across ${siteConfig.contact.address.state} — including Round Rock, Cedar Park, San Antonio, Houston, and Dallas–Fort Worth — as well as remote clients throughout the United States.`,

          },
          {
            q: "Is the strategy call really free?",
            a: "Yes. The initial 30-minute strategy call is free and includes a mini-audit of your organic footprint with three specific, actionable opportunities.",
          },
          {
            q: "What happens immediately after I submit the form?",
            a: `Your message is routed straight to our ${siteConfig.contact.address.city} team, not a queue or a CRM drip sequence. A person reads it, checks your site and current rankings, and replies from an @optimizeplus.net address — usually with either a call link or a few clarifying questions if your message was thin on detail.`,
          },
          {
            q: "What should I prepare before the strategy call?",
            a: "Bring your website address, admin or view access to Google Search Console and Analytics if you have it, a rough sense of your current monthly organic traffic, and a plain-language answer to what growth would actually mean for the business — more calls, more bookings, more qualified pipeline, or something else.",
          },
          {
            q: "How does Optimize Plus price engagements?",
            a: "We do not publish flat rate cards because the right scope depends on your current footprint, competitive set, and how many services or locations you operate. We quote after the audit, not before, so the number reflects real work rather than a guess.",
          },
          {
            q: `Who is ${siteConfig.name} not a good fit for?`,
            a: "We are not the right fit for businesses expecting guaranteed page-one rankings on a fixed timeline, for anyone wanting a one-off technical fix with no ongoing content or authority work, or for sites that cannot commit to at least a few months of sustained effort.",
          },
        ]),
      ),
    };
  },
  component: ContactPage,
});

// Zod schema — mirrored client + (future) server side.
const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Please enter your full name (at least 2 characters).")
    .max(100, "Name must be under 100 characters."),
  email: z
    .string()
    .trim()
    .min(1, "Email is required.")
    .email("Please enter a valid work email address.")
    .max(255, "Email must be under 255 characters."),
  company: z
    .string()
    .trim()
    .max(150, "Company/website must be under 150 characters.")
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .trim()
    .min(20, "Please share a little more — at least 20 characters — so we can prepare a useful reply.")
    .max(2000, "Message must be under 2000 characters."),
  // Honeypot: real users leave this empty; bots often fill every field.
  website_url: z.string().max(0, "Spam detected.").optional().or(z.literal("")),
  // Timestamp check — real users take at least ~2 seconds to fill a form.
  ts: z.coerce.number(),
});

type FieldErrors = Partial<Record<
  "name" | "email" | "company" | "message" | "form",
  string
>>;

function ContactPage() {
  const search = Route.useSearch();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const mountedAt = useRef<number>(Date.now());
  const startedRef = useRef(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const nameId = useId();
  const emailId = useId();
  const companyId = useId();
  const messageId = useId();

  // Resolve ?location= and ?service= into a friendly, prefilled message.
  // Supported location slugs: any Austin neighborhood or top-level city.
  const prefill = useMemo(() => {
    const neigh = search.location
      ? AUSTIN_NEIGHBORHOODS.find((n) => n.slug === search.location)
      : undefined;
    const loc = search.location
      ? LOCATIONS.find((l) => l.slug === search.location)
      : undefined;
    const svc = search.service
      ? SERVICES.find((s) => s.slug === search.service)
      : undefined;

    const locationLabel = neigh
      ? `${neigh.name} (Austin ${neigh.zips.join(", ")})`
      : loc
        ? `${loc.city}, ${siteConfig.contact.address.state}`
        : "";
    const serviceLabel = svc ? svc.title : "";

    if (!locationLabel && !serviceLabel) return { message: "", banner: "" };

    const parts: string[] = [];
    parts.push(
      `Hi ${siteConfig.name} team — I'm reaching out about ${
        serviceLabel ? serviceLabel.toLowerCase() : "SEO"
      }${locationLabel ? ` for a business in ${locationLabel}` : ""}.`,
    );
    parts.push("");
    parts.push("A little context on where we are today:");
    parts.push("• Current organic traffic / rankings: ");
    parts.push("• What we've already tried: ");
    parts.push("• The specific outcome we want in the next 90 days: ");
    return {
      message: parts.join("\n"),
      banner: locationLabel
        ? `Starting a conversation about ${serviceLabel || "SEO"} in ${locationLabel}.`
        : `Starting a conversation about ${serviceLabel}.`,
    };
  }, [search.location, search.service]);


  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const raw = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      company: String(fd.get("company") ?? ""),
      message: String(fd.get("message") ?? ""),
      website_url: String(fd.get("website_url") ?? ""),
      ts: String(fd.get("ts") ?? "0"),
    };

    // Bot-check: submitted in under 2 seconds → almost certainly automated.
    const elapsed = Date.now() - Number(raw.ts || 0);
    if (elapsed < 2000) {
      setErrors({ form: "Something went wrong. Please try again in a moment." });
      return;
    }

    const parsed = contactSchema.safeParse(raw);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "");
        if (key === "website_url" || key === "ts") {
          next.form = "Spam protection triggered. Please refresh and try again.";
        } else if (
          (key === "name" || key === "email" || key === "company" || key === "message") &&
          !next[key]
        ) {
          next[key] = issue.message;
        }
      }
      setErrors(next);
      // Move focus to the accessible error summary.
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }


    setStatus("submitting");
    try {
      const res = await fetch("/api/public/leads", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...parsed.data,
          source_path: typeof window !== "undefined" ? window.location.pathname : "/contact/",
          service_slug: search.service ?? "",
          location_slug: search.location ?? "",
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !body.ok) {
        setErrors({
          form:
            body.error ??
            "We couldn't send that. Please email info@optimizeplus.net and we'll pick it up right away.",
        });
        setStatus("idle");
        requestAnimationFrame(() => summaryRef.current?.focus());
        return;
      }
      trackConversion("lead_submit", {
        label: search.service ? `service:${search.service}` : "contact-form",
      });
      setStatus("success");
    } catch {
      setErrors({
        form: "Network error. Please email info@optimizeplus.net and we'll pick it up right away.",
      });
      setStatus("idle");
      requestAnimationFrame(() => summaryRef.current?.focus());
    }
  }

  const errorList = Object.entries(errors).filter(([, v]) => Boolean(v));

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1} className="container-page py-20 md:py-28">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] items-start">
          <div>
            <SectionHeading
              as="h1"
              eyebrow="Contact"
              title={`Contact ${siteConfig.name} — book your free strategy call.`}
              intro="Tell us about your organic goals. If we're a fit, we'll book a free 30-minute strategy call and prepare a mini-audit of your current Google, ChatGPT, and Perplexity footprint."

            />
            <Picture
              variantKey="austin"
              alt=""
              className="mt-8 rounded-2xl overflow-hidden border border-border block"
              imgClassName="w-full h-auto aspect-[16/9] object-cover"
              sizes="(min-width: 1024px) 600px, 100vw"
            />
            <dl className="mt-10 space-y-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                  Email
                </dt>
              <dd>
                  <a
                    href="mailto:info@optimizeplus.net"
                    className="text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                  >
                    info@optimizeplus.net
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                  Phone
                </dt>
                <dd>
                  <a
                    href="tel:+17373210551"
                    className="text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                  >
                    {siteConfig.contact.whatsapp.display}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                  Headquarters
                </dt>
                <dd className="not-italic">
                  {siteConfig.contact.address.street}, {siteConfig.contact.address.city}, {siteConfig.contact.address.state} {siteConfig.contact.address.zip} · <a href="https://maps.app.goo.gl/L67bz47dQgtJs4Ba8" target="_blank" rel="noopener noreferrer" className="story-link">View on Google Maps</a> · serving clients across {siteConfig.contact.address.state} &amp; the US
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                  Availability
                </dt>
                <dd>24/7 services · reply within 1 business day</dd>
              </div>
            </dl>
          </div>

          {status === "success" ? (
            <div
              role="status"
              aria-live="polite"
              className="surface-card p-8 space-y-3"
            >
              <h2 className="font-display text-2xl">Thanks — message received.</h2>
              <p className="text-sm text-muted-foreground">
                We'll reply from an ${siteConfig.name} email address within one
                business day. In the meantime, feel free to browse our{" "}
                <a href="/case-studies" className="text-primary hover:underline">
                  case studies
                </a>{" "}
                or{" "}
                <a href="/services" className="text-primary hover:underline">
                  services
                </a>
                .
              </p>
            </div>
          ) : (
            <form
              ref={formRef}
              noValidate
              onSubmit={handleSubmit}
              onFocusCapture={() => {
                if (!startedRef.current) {
                  startedRef.current = true;
                  trackConversion("form_start", { label: "contact-form" });
                }
              }}
              aria-labelledby="contact-form-heading"
              className="surface-card p-8 space-y-5"
            >
              {prefill.banner && (
                <div
                  role="status"
                  className="rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-foreground/90"
                >
                  <span className="text-primary font-medium">Context locked in:</span>{" "}
                  {prefill.banner} We pre-drafted a starting message — edit anything below.
                </div>
              )}
              <h2 id="contact-form-heading" className="sr-only">
                Contact form
              </h2>

              {/* Honeypot — visually hidden but reachable to bots */}
              <div aria-hidden="true" className="hidden">
                <label htmlFor="website_url">
                  Do not fill this in
                </label>
                <input
                  id="website_url"
                  name="website_url"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <input type="hidden" name="ts" defaultValue={mountedAt.current} />

              {errorList.length > 0 && (
                <div
                  ref={summaryRef}
                  role="alert"
                  tabIndex={-1}
                  className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                >
                  <p className="font-semibold text-destructive">
                    Please fix the following before submitting:
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-1 text-destructive/90">
                    {errorList.map(([key, msg]) => (
                      <li key={key}>
                        {key === "form" ? (
                          msg
                        ) : (
                          <a
                            href={`#${
                              key === "name"
                                ? nameId
                                : key === "email"
                                ? emailId
                                : key === "company"
                                ? companyId
                                : messageId
                            }`}
                            className="underline"
                          >
                            {msg}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Field
                id={nameId}
                label="Name"
                name="name"
                autoComplete="name"
                required
                error={errors.name}
                maxLength={100}
              />
              <Field
                id={emailId}
                label="Work email"
                name="email"
                type="email"
                autoComplete="email"
                required
                error={errors.email}
                maxLength={255}
              />
              <Field
                id={companyId}
                label="Company / website"
                name="company"
                autoComplete="organization"
                error={errors.company}
                maxLength={150}
              />
              <div>
                <label
                  htmlFor={messageId}
                  className="text-xs uppercase tracking-widest text-muted-foreground"
                >
                  What are you trying to grow?
                  <span className="text-primary"> *</span>
                </label>
                <textarea
                  id={messageId}
                  name="message"
                  rows={7}
                  defaultValue={prefill.message}
                  required
                  maxLength={2000}
                  aria-invalid={errors.message ? "true" : "false"}
                  aria-describedby={
                    errors.message ? `${messageId}-err` : `${messageId}-hint`
                  }
                  className={`mt-2 w-full rounded-lg bg-background border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    errors.message ? "border-destructive" : "border-input"
                  }`}
                  placeholder="e.g. Move from 500 to 5K monthly organic visits for our Austin HVAC service area, and start getting cited by ChatGPT for 'HVAC repair Austin'."
                />
                {errors.message ? (
                  <p
                    id={`${messageId}-err`}
                    role="alert"
                    className="mt-1.5 text-xs text-destructive"
                  >
                    {errors.message}
                  </p>
                ) : (
                  <p
                    id={`${messageId}-hint`}
                    className="mt-1.5 text-xs text-muted-foreground"
                  >
                    20–2000 characters. The more specific, the more useful our reply.
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-70"
              >
                {status === "submitting"
                  ? "Sending…"
                  : "Request a strategy call →"}
              </button>
              <p className="text-[0.7rem] text-muted-foreground">
                By submitting you agree to receive one follow-up email from
                Optimize Plus. We never share your details.
              </p>
            </form>
          )}
        </div>

        <section aria-labelledby="after-submit-heading" className="mt-28 max-w-3xl">
          <SectionHeading
            as="h2"
            eyebrow="What to expect"
            title="What actually happens after you submit this form."
            intro="Most agency contact pages promise a callback and leave it there. Here is the honest sequence, from the moment you hit send to the first weeks of an engagement — so there is no ambiguity about what comes next."
          />
          <div className="mt-8 space-y-6 text-sm leading-relaxed text-foreground/85">
            <p>
              The moment you submit, your message lands directly with our Austin team — no auto-responder pretending to be a human, no offshore intake desk reading from a script. Someone on our side opens your site, runs a quick check on current organic visibility, and skims for anything obviously broken (a noindex tag left on, a robots.txt block, a Search Console property that has never been verified). That first pass takes a few minutes and shapes the reply you get.
            </p>
            <p>
              If your message gave us enough to work with, the reply includes a link to book a 30-minute strategy call and one or two specific observations about your current organic presence — something concrete, not a canned compliment. If your message was thin (just "interested in SEO" with no URL or context), we will ask a couple of clarifying questions first rather than book a call blind. We would rather spend five extra minutes upfront than waste thirty minutes of your time on a call where we are still asking basic questions.
            </p>
          </div>

          <h3 className="mt-10 font-display text-2xl">Response times</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            We reply to every submission within one business day, and most get a reply the same day if you submit before mid-afternoon Central time. Weekend and evening submissions are read and answered by the next business morning. If you have an urgent situation — a manual action, a sudden traffic drop, a site that has been compromised — say so in the message itself; we prioritise those over routine inquiries and will often respond within a few hours regardless of the day.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            "24/7 services" on our contact details refers to ongoing engagement support — monitoring, incident response for existing clients, and international team coverage — not that a strategist is standing by around the clock for first-touch inquiries. New enquiries are handled during Central time business hours, which keeps replies thoughtful rather than templated.
          </p>

          <h3 className="mt-10 font-display text-2xl">What to prepare for the call</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            The call is more useful the less time we spend on discovery basics. Before it, have these ready:
          </p>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span>Your website URL and, if possible, view or admin access to Google Search Console and Google Analytics — even a screenshot of the last three months of data helps.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span>A rough sense of current monthly organic traffic and whether it has been flat, growing, or declining over the past six to twelve months.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span>What you have already tried — a previous agency, an in-house effort, a content push — and what did or did not move the needle.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span>A specific business outcome you care about — leads, bookings, calls, revenue from organic — rather than "more traffic" as an abstract goal.</span></li>
            <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden /><span>Who else needs to sign off on hiring an agency, and roughly when you would want work to start.</span></li>
          </ul>

          <h3 className="mt-10 font-display text-2xl">Engagement types</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            Not every business needs the same shape of engagement. We generally work in three modes, and the strategy call is where we figure out which one fits:
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">Ongoing programme</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Monthly retained work covering technical SEO, content, authority building, and AI-search visibility. Right for businesses treating organic as a compounding channel rather than a project with an end date.</p>
            </div>
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">Defined project</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">A scoped piece of work with a clear start and finish — a technical migration audit, a site restructure, a schema implementation. Right when the need is specific and time-bound rather than continuous.</p>
            </div>
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">Advisory / fractional</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">Strategic oversight for teams that have in-house resource to execute but need direction, prioritisation, and an outside check on the work. Right for marketing teams that already have hands, but not a specialist head.</p>
            </div>
          </div>

          <h3 className="mt-10 font-display text-2xl">How we think about pricing</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            We do not publish a rate card because the honest price depends on variables a generic number cannot capture: how many locations or service lines you operate, how competitive your market is, how much technical debt is on the site, and how quickly you want to move. A single-location service business with a clean site needs a different scope — and a different investment — than a multi-location brand competing in a saturated category.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            What we will not do is quote a number before we have looked at your site. A price given without an audit is a guess dressed up as a proposal, and guesses tend to be wrong in the agency's favour. Our proposals follow the audit, reference the specific work identified, and let you compare the scope against the number rather than take the number on faith.
          </p>

          <h3 className="mt-10 font-display text-2xl">Where we work</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            We are headquartered in Austin, Texas, and most of our local SEO and Google Business Profile work centres on Austin and the surrounding Central Texas metro — Round Rock, Cedar Park, Georgetown, Pflugerville, and the wider Hill Country. Beyond Central Texas, we work with clients across the state, including San Antonio, Houston, and Dallas–Fort Worth, and with remote clients throughout the United States for organic search and AI-search work that does not depend on physical proximity — technical SEO, content strategy, and schema implementation travel well regardless of time zone.
          </p>

          <h3 className="mt-10 font-display text-2xl">Who we are not the right fit for</h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            We would rather tell you this upfront than waste your time on a call that goes nowhere. We are not a good fit if you are looking for a guarantee of a specific ranking position by a specific date — no agency controls the algorithm closely enough to promise that honestly. We are not a good fit for a single technical fix with no appetite for ongoing content or authority work, because technical health without a content and authority strategy behind it rarely moves the needle on its own. And we are not a good fit if the business cannot commit to at least a few months of sustained effort — organic search rewards compounding work, and a one-month sprint is rarely enough to show what the channel can actually do.
          </p>

          <h3 className="mt-10 font-display text-2xl">
            Researching a specific service or city first?
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            This page is for starting a conversation. If you are still comparing
            providers, the pages below cover scope, deliverables, timelines and
            local market detail in full — they are the better place to read
            before you get in touch.
          </p>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">
                Services
              </div>
              <ul className="mt-3 grid gap-2 text-sm">
                {SERVICES.slice(0, 8).map((s) => (
                  <li key={s.slug}>
                    <Link
                      to="/services/$slug/"
                      params={{ slug: s.slug }}
                      className="text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
                    >
                      {s.title} in Austin, TX
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/services/"
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    All SEO &amp; digital marketing services →
                  </Link>
                </li>
              </ul>
            </div>
            <div className="surface-card p-5">
              <div className="text-primary text-xs uppercase tracking-widest">
                Locations
              </div>
              <ul className="mt-3 grid gap-2 text-sm">
                {LOCATIONS.slice(0, 8).map((l) => (
                  <li key={l.slug}>
                    <Link
                    to={"/seo-agency-{$slug}" as any}
                    params={{ slug: l.slug } as any}
                      className="text-foreground/85 underline-offset-4 hover:text-primary hover:underline"
                    >
                      SEO agency in {l.city}, {l.region}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/locations/"
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    All Texas locations we serve →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  id,
  label,
  name,
  type = "text",
  required,
  autoComplete,
  error,
  maxLength,
}: {
  id: string;
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  error?: string;
  maxLength?: number;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs uppercase tracking-widest text-muted-foreground"
      >
        {label}
        {required && <span className="text-primary"> *</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-err` : undefined}
        className={`mt-2 w-full rounded-lg bg-background border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          error ? "border-destructive" : "border-input"
        }`}
      />
      {error && (
        <p
          id={`${id}-err`}
          role="alert"
          className="mt-1.5 text-xs text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}
